export type FolderPermissionDialogVariant =
  | 'public-folder'
  | 'private-folder'
  | 'teamspace-folder'
  | 'teamspace-root';
export type FolderCreatePermissionVariant =
  | FolderPermissionDialogVariant
  | undefined;
export type FolderPermissionVisibility = 'public' | 'private';
export type FolderPermissionDialogMode = 'create' | 'share';

export interface FolderPermissionDialogPolicy {
  defaultInheritPermissions: boolean;
  defaultVisibility: FolderPermissionVisibility;
  footerKind: 'folder' | 'private-folder' | 'teamspace-folder';
  showInheritanceSection: boolean;
  showVisibilitySection: boolean;
  variant: FolderPermissionDialogVariant;
  visibilityLockedTo: FolderPermissionVisibility | null;
}

export const getFolderPermissionDialogPolicy = (
  variant: FolderCreatePermissionVariant
): FolderPermissionDialogPolicy => {
  switch (variant) {
    case 'private-folder':
      return {
        defaultInheritPermissions: true,
        // Area placement is an independent access ceiling. Keep the ordinary
        // folder default visible so a later move back to Shared preserves the
        // author's explicit visibility choice.
        defaultVisibility: 'public',
        footerKind: 'private-folder',
        showInheritanceSection: true,
        showVisibilitySection: true,
        variant: 'private-folder',
        visibilityLockedTo: null,
      };
    case 'teamspace-folder':
      return {
        defaultInheritPermissions: true,
        defaultVisibility: 'public',
        footerKind: 'teamspace-folder',
        showInheritanceSection: true,
        showVisibilitySection: true,
        variant: 'teamspace-folder',
        visibilityLockedTo: null,
      };
    case 'teamspace-root':
      return {
        defaultInheritPermissions: false,
        defaultVisibility: 'private',
        footerKind: 'teamspace-folder',
        showInheritanceSection: true,
        showVisibilitySection: true,
        variant: 'teamspace-root',
        visibilityLockedTo: null,
      };
    case 'public-folder':
    case undefined:
      return {
        defaultInheritPermissions: true,
        defaultVisibility: 'public',
        footerKind: 'folder',
        showInheritanceSection: true,
        showVisibilitySection: true,
        variant: 'public-folder',
        visibilityLockedTo: null,
      };
  }
};

export const folderPermissionDialogHasAdvancedSettings = (
  policy: FolderPermissionDialogPolicy
) => policy.showVisibilitySection || policy.showInheritanceSection;

export const resolveFolderPermissionVisibility = (
  policy: FolderPermissionDialogPolicy,
  visibility: FolderPermissionVisibility
): FolderPermissionVisibility => policy.visibilityLockedTo ?? visibility;

export const getDefaultFolderCreateSettings = (
  variant: FolderCreatePermissionVariant
) => {
  const policy = getFolderPermissionDialogPolicy(variant);
  return {
    inheritPermissions: policy.defaultInheritPermissions,
    visibility: resolveFolderPermissionVisibility(
      policy,
      policy.defaultVisibility
    ),
  };
};
